import { memo, useCallback, useState } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentGuidedVariantSettingsActions } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSettingsActions';
import { WorkspaceAgentGuidedVariantSizeSavePopover } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeSavePopover';
import { WorkspaceAgentGuidedVariantSmartResizeField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSmartResizeField';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import {
  buildWorkspaceAgentSmartResizePrompt,
  selectionIsSavedSize,
  selectionMatchesSavedSet,
  WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES,
  type WorkspaceAgentSmartResizeSize,
  workspaceAgentSmartResizeSizeLabel,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';
import {
  canSaveWorkspaceAgentSmartResizeSelection,
  type WorkspaceAgentSmartResizeSavedCatalog,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResizeSavedCatalog';

export type WorkspaceAgentGuidedVariantSmartResizeSettingsProps = {
  imageCount: number;
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => void;
  savedCatalog: WorkspaceAgentSmartResizeSavedCatalog;
  skippedFileCount: number;
};

export const WorkspaceAgentGuidedVariantSmartResizeSettings = memo(
  ({ imageCount, onGenerate, savedCatalog, skippedFileCount }: WorkspaceAgentGuidedVariantSmartResizeSettingsProps) => {
    const [sizes, setSizes] = useState<WorkspaceAgentSmartResizeSize[]>([]);
    const { isLoading: isSavedCatalogLoading, onSave, sets: savedSets, sizes: savedSizes } = savedCatalog;
    const outputCount = imageCount * sizes.length;
    const isGenerateDisabled =
      imageCount < 1 || sizes.length < 1 || sizes.length > WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES;
    const generateLabel = isGenerateDisabled ? 'Generate' : `Generate ${outputCount.toLocaleString()}`;
    // A ratio stays in the resize selection, but the catalog has no field for it. Save set stays
    // visible, and the saved payload is the pixel sizes only.
    const savableSizes = sizes.filter((size) => canSaveWorkspaceAgentSmartResizeSelection([size]));
    const showSave =
      savableSizes.length > 0 &&
      !selectionIsSavedSize(sizes, savedSizes) &&
      !selectionMatchesSavedSet(sizes, savedSets) &&
      !isSavedCatalogLoading &&
      onSave !== undefined;

    const handleGenerate = useCallback(() => {
      if (imageCount < 1 || sizes.length < 1 || sizes.length > WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES) {
        return;
      }

      onGenerate({
        allowsJpeg: true,
        kind: 'content',
        build: (destination, output) =>
          buildWorkspaceAgentSmartResizePrompt({
            destination,
            imageCount,
            output,
            sizes,
            skippedFileCount,
          }),
        structuredDisplay: {
          description: sizes.map((size) => workspaceAgentSmartResizeSizeLabel(size)).join(', '),
          icon: 'sparkles',
          title: 'Smart resize',
        },
      });
    }, [imageCount, onGenerate, sizes, skippedFileCount]);

    const handleSaveName = useCallback(
      async (name: string): Promise<boolean> => {
        if (savableSizes.length < 1 || !onSave) {
          return false;
        }

        return onSave({ name, sizes: savableSizes });
      },
      [onSave, savableSizes],
    );

    return (
      <div className="flex flex-col gap-3" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SETTINGS">
        <WorkspaceAgentGuidedVariantSmartResizeField
          isSavedCatalogLoading={isSavedCatalogLoading}
          onSizesChange={setSizes}
          savedSets={savedSets}
          savedSizes={savedSizes}
          sizes={sizes}
        />
        <div className={cn('flex items-center gap-2', showSave ? 'justify-between' : 'justify-end')}>
          {showSave ? (
            <WorkspaceAgentGuidedVariantSizeSavePopover onSave={handleSaveName} sizeCount={savableSizes.length} />
          ) : null}
          <WorkspaceAgentGuidedVariantSettingsActions
            generateLabel={generateLabel}
            isGenerateDisabled={isGenerateDisabled}
            onGenerate={handleGenerate}
          />
        </div>
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantSmartResizeSettings.displayName = 'WorkspaceAgentGuidedVariantSmartResizeSettings';
